import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    CommonModal as Modal,
    Input,
    PrimaryButton,
    Select,
    AddButton
} from '../../Common';
import {
    Grid,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Paper,
    Typography,
    IconButton,
    TableContainer
} from '@mui/material';
import { Skeleton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import { GetLocationCodes } from "../../../CodesCache";

const SubLocation = ({
    id = ''
}) => {
    const [data, setData] = useState([]);
    const [rowState, setRowState] = useState({});
    const [parentLocation, setParentLocation] = useState({});
    const [codes, setCodes] = useState([]);
    const [addingChild, setAddingChild] = useState(false);
    const [sublocationData, setSublocationData] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [editing, setIsEditing] = useState(false);
    const tableCell = { borderBottom: "none", paddingLeft: '3%' };

    const typeMappings = {
        1: "Building",
        2: "Area / Wing",
        3: "Floor",
        4: "Suite",
        5: "Room",
    };
    const tableHeaders = [{ key: 'shortName', value: 'NAME' }, { key: 'type', value: 'TYPE' }, { key: '', value: '' }];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(Routes.Location.SubLocations(id));
                const json = await response.json();
                const { subLocationTypes = [] } = await GetLocationCodes();
                setData(json);
                setCodes(subLocationTypes);
            } catch (error) {
                console.log('Error fetching data', error);
                toast.error('Something went wrong!', { duration: 4000 });
            }
        };

        fetchData();
    }, [id]);

    const renderNewLocationModal = (row = {}, isAddingChild = false) => {
        if (isAddingChild) {
            setAddingChild(true);
            setParentLocation(row);
        }
        return setOpenModal(true);
    };

    const closeModal = () => {
        resetFields();
        setOpenModal(false);
    };

    const resetFields = () => {
        setIsEditing(false);
        setAddingChild(false);
        return setSublocationData({});
    };

    const handleSublocationDataChange = (e, key) => setSublocationData(
        prev => ({ ...prev, [key]: e.target.value })
    );

    const submit = async () => {
        const response = await fetch(Routes.Location.SaveSubLocations(id), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })

        if (response.status === 200) {
            const newdata = await response.json();
            setData(newdata);
        } else {
            console.log('Error Response', response);
            toast.error('Error Saving New Sublocation', { duration: 4000 });
        }
        return resetFields();
    };

    const addParent = async (parent_data) => {
        const { shortName = '', type = null } = parent_data;
        var newparent = {
            id: 0,
            type,
            parentId: null,
            shortName,
            fullPath: '',
            level: 1,
            enabled: true,
            children: [],
        };

        var temp = data;
        temp.push(newparent);

        setData(temp);
        closeModal();
        await submit();
        return toast.success('Sublocation Added Successfully', { duration: 4000 });
    };

    const addChild = async (new_child, parent) => {
        const { shortName = '', type = null } = new_child;
        const { id: parent_id = '', level: parent_level = null, children = [] } = parent;
        const newchild = {
            id: 0,
            type,
            parentId: parent_id,
            shortName,
            fullPath: parent.fullPath,
            level: parent_level + 1,
            enabled: true,
            children: [],
        };
        children.push(newchild);

        closeModal();
        setData(data);
        await submit();
        return toast.success('Sublocation Added Successfully', { duration: 4000 });
    };

    const handleSubmit = () => {
        // type & level are the same thing, they can be used interchangeably
        const dataToAdd = { shortName: sublocationData.shortName, type: sublocationData.level };
        if (addingChild) {
            return addChild(dataToAdd, parentLocation);
        }
        return addParent(dataToAdd);
    };

    const handleDelete = async (row) => {
        row.enabled = false;
        row.changed = true;
        row.error = false;
        await submit();
        return toast.success('Sublocation Deleted Successfully', { duration: 4000 });
    };

    const handleEdit = (row) => {
        setIsEditing(true);
        setSublocationData(row);
        setOpenModal(true);
    };

    const saveEdit = async (row) => {
        const { id: edited_row_id } = row;
        // start at the top sublocation, drill down into it's children until you find
        // the reocrd that was updated, when found we are updating the values
        const edited_nested_row = (arr) => (
            arr.map((item, index) => {
                if (item.id === edited_row_id) {
                    return arr[index] = row;
                }
                return edited_nested_row(item.children);
            })
        );

        edited_nested_row(data);
        closeModal();
        await submit();
        resetFields();
        return toast.success('Sublocation Edited Successfully', { duration: 4000 });
    };

    const determineTypeOptions = () => {
        if (editing) {
            return codes.filter(c => c.level >= sublocationData.level);
        }

        if (addingChild) {
            return codes.filter(c => c.level > parentLocation.level);
        }
        return codes;
    };

    const RowComponent = ({ data, loading }) => {
        const cursor = { cursor: 'pointer' };

        const handleRowOpen = (id) => {
            const newstate = { ...rowState, [id]: !rowState[id] };
            setRowState(newstate);
        };

        return (
            <>
                {loading ? (
                    tableHeaders.map(() => (
                        <TableRow>
                            <TableCell style={tableCell}>
                                <Skeleton />
                            </TableCell>
                            <TableCell style={tableCell}>
                                <Skeleton />
                            </TableCell>
                            <TableCell style={tableCell}>
                                <Skeleton />
                            </TableCell>
                        </TableRow>
                    ))
                ) : (
                    data.filter(location => location.enabled === true).map((row, index) => {
                        const {
                            shortName = '',
                            children = [],
                            level = 1,
                            type = null,
                            id = '',
                            parentId = null,
                        } = row;
                        const rowColor = index % 2 ? { background : "rgba(216,216,216,0.15)" } : { background : "#FFF" };
                        const isParent = parentId === null;
                        const canUserAddOrDelete = !UserInfo.isReadOnly() && UserInfo.isNativeUser();
                        const locationNameStyle = {
                            marginLeft: `${isParent ? '' : `${level * 5}%`}`,
                            color: '#008299',
                            fontWeight: 'bold',
                            fontFamily: 'Open Sans',
                        };
                        return (
                            <>
                                <TableRow key={id} style={rowColor}>
                                    <TableCell style={tableCell}>
                                        <Typography
                                            variant="subtitle2"
                                            style={locationNameStyle}
                                        >
                                            {shortName}
                                            {children.length > 0 && (
                                                <IconButton
                                                    aria-label="expand row"
                                                    size="small"
                                                    onClick={() => handleRowOpen(id)}
                                                >
                                                    {rowState[id] ? <ExpandLessIcon style={cursor} /> : <ExpandMoreIcon style={cursor} />}
                                                </IconButton>
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell style={tableCell}>
                                        <Typography
                                            variant="subtitle2"
                                        >
                                            {typeMappings[`${type}`]}
                                        </Typography>
                                    </TableCell>
                                    <TableCell style={tableCell}>
                                        {canUserAddOrDelete && (
                                            <>
                                                <IconButton aria-label="edit" onClick={() => handleEdit(row)}>
                                                    <CreateIcon />
                                                </IconButton>
                                                {level < 4 && (
                                                    <IconButton aria-label="add" onClick={() => renderNewLocationModal(row, true)}>
                                                        <AddIcon />
                                                    </IconButton>
                                                )}
                                                <IconButton aria-label="delete" onClick={() => handleDelete(row)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                                {rowState[id] && <RowComponent data={children} loading={loading} />}
                            </>
                        );
                    })
                )}
            </>
        );
    };

    return (
        <>
            <TableContainer component={Paper} style={{ marginTop: '3%' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" style={tableCell} width="33%">
                                <Typography
                                    variant="h6"
                                    style={{ marginTop: "2%" }}
                                >
                                    Sublocations
                                </Typography>
                            </TableCell>
                            <TableCell style={tableCell} width="34%" />
                            <TableCell align="right" style={tableCell} width="33%">
                                <AddButton
                                    text='Add Sublocation'
                                    onClick={() => setOpenModal(true)}
                                    disabled={
                                        UserInfo.isReadOnly() || 
                                        UserInfo.isNativeAccountServiceProvider() ||
                                        !(UserInfo.isSystemOwner() && 
                                            (
                                                UserInfo.isAdminRole() ||
                                                UserInfo.isRegionalAdministrator()
                                            )
                                        )
                                    }
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            {tableHeaders.map(header => (
                                <TableCell style={tableCell}>
                                    <Typography
                                        variant="subtitle2"
                                        style={{ fontSize: "12px" }}
                                    >
                                        {header.value}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <RowComponent data={data} /> 
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal
                header={`${editing ? 'Edit' : 'Add New'} Sublocation`}
                open={openModal}
                close={closeModal}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} align='center'>
                        <Input
                            required
                            fullWidth
                            variant='standard'
                            label='Sublocation Name'
                            value={sublocationData.shortName}
                            onChange={e => handleSublocationDataChange(e, 'shortName')}
                            error={!sublocationData.shortName}
                            sx={{ width: '80%' }}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <Select
                            required
                            variant='standard'
                            label='Type'
                            value={sublocationData.level}
                            onChange={e => handleSublocationDataChange(e, 'level')}
                            options={determineTypeOptions()}
                            width={80}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <PrimaryButton
                            buttonText={`${editing ? 'Edit' : 'Add New'} Sublocation`}
                            onClick={editing ? () => saveEdit(sublocationData) : () => handleSubmit()}
                            disabled={!sublocationData.shortName || !sublocationData.level}
                        />
                    </Grid>
                </Grid>
            </Modal>
        </>
    );
};

export default SubLocation;
